import { twMerge as cn } from 'tailwind-merge';
import { DEFAULT_POSTGRES_SCHEMA, POSTGRES_MAJOR_VERSIONS } from '../../types/onboarding';
import { Input } from './Input';
import { Label } from './Label';

interface PostgresSettingsFieldsProps {
  /** Major version as a string ('' when not chosen yet). */
  version: string;
  schemaName: string;
  onVersionChange: (version: string) => void;
  onSchemaNameChange: (schemaName: string) => void;
  versionError?: string;
  schemaNameError?: string;
  disabled?: boolean;
  /** The schema can only be set when the project becomes a PostgreSQL project. */
  schemaDisabled?: boolean;
}

/** PostgreSQL version and schema, shown in project onboarding when PostgreSQL is selected. */
export function PostgresSettingsFields({
  version,
  schemaName,
  onVersionChange,
  onSchemaNameChange,
  versionError,
  schemaNameError,
  disabled = false,
  schemaDisabled = false,
}: PostgresSettingsFieldsProps) {
  return (
    <div className="space-y-3 rounded-md border border-gray-200 p-3">
      <p className="text-xs text-gray-500">
        Supported: RDS PostgreSQL and Aurora PostgreSQL, versions{' '}
        {POSTGRES_MAJOR_VERSIONS[0]}–{POSTGRES_MAJOR_VERSIONS[POSTGRES_MAJOR_VERSIONS.length - 1]}.
        RPT&apos;s test databases run the same major version.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="postgresVersion">
            PostgreSQL Version <span className="text-red-500">*</span>
          </Label>
          <select
            id="postgresVersion"
            value={version}
            disabled={disabled}
            onChange={(e) => onVersionChange(e.target.value)}
            className={cn(
              'flex h-9 w-full rounded-md border bg-white px-3 py-1 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-transparent',
              'disabled:cursor-not-allowed disabled:opacity-50',
              versionError ? 'border-red-500' : 'border-gray-200'
            )}
          >
            <option value="" disabled>
              Select version
            </option>
            {POSTGRES_MAJOR_VERSIONS.map((major) => (
              <option key={major} value={String(major)}>
                {major}
              </option>
            ))}
          </select>
          {versionError && <p className="text-sm text-red-500">{versionError}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="postgresSchema">Schema</Label>
          <Input
            id="postgresSchema"
            value={schemaName}
            placeholder={DEFAULT_POSTGRES_SCHEMA}
            disabled={disabled || schemaDisabled}
            onChange={(e) => onSchemaNameChange(e.target.value)}
            className={schemaNameError ? 'border-red-500' : undefined}
          />
          {schemaNameError && <p className="text-sm text-red-500">{schemaNameError}</p>}
        </div>
      </div>
    </div>
  );
}
